import { Form, Formik } from 'formik'
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react'
import { toast } from 'react-toastify'
import Button from 'src/components/Button'
import TableMui from 'src/components/TableMui'
import FlexLayout from 'src/components/FlexLayout'
import FormField from 'src/components/Form/FormField'
import Loading from 'src/components/Loading'
import Text from 'src/components/Text'
import { TrashButton } from 'src/components/TrashButton'
import { useFulfillmentCenter } from 'src/hooks/useFulfillmentCenter'
import {
  getFractionableBatch,
  getFractionableBatches,
} from 'src/services/receivement/receivementService'
import '../../../App.css'
import FractionLot from '../components/FractionLot/FractionLot'
import useDeleteBatch from '../components/ModalDeleteBatch/useDeleteBatch'
import { Batch } from '../FractionTypes'
import * as S from './Fraction.styles'
import { formatBatchQuantities } from '../FractionUtils'
import { useDispatch } from 'react-redux'
import { actions } from '../FractionState'
import RaspberryIcon from '../../../assets/images/other-images/raspberry-pi.svg'
import { useSelector } from 'src/utils/store'
import PrinterInfo from '../components/PrinterInfo'
import { PrinterData } from 'src/features/settings/printers-management/PrintersManagementTypes'
import { PauseFractioning } from '../components/PauseFractioning'

const DEFAULT_PER_PAGE = 20
const ROWS_PER_PAGE_OPTIONS = [20, 30, 50, 100, 200, 500]
const DEFAULT_ORDER = 'desc'
const DEFAULT_ORDER_BY = 'data_inclusao'

interface Pagination {
  currentPage: number
  perPage: number
  totalItems: number
  totalPages: number
}

interface BatchesQuery {
  page: number
  perPage: number
  order: string
  orderBy: string
}

const Fraction = () => {
  const [dataTable, setDataTables] = useState([])
  const [receivementId, setReceivementId] = useState<number | undefined>()
  const [modelId, setModelId] = useState<number | undefined>()
  const [load, setLoad] = useState(false)
  const [isFetchingBatches, setIsFetchingBatches] = useState(false)
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    perPage: DEFAULT_PER_PAGE,
    totalItems: 0,
    totalPages: 0,
  })
  const lastQuery = useRef<BatchesQuery>({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    order: DEFAULT_ORDER,
    orderBy: DEFAULT_ORDER_BY,
  })
  const refetchCurrentPage = useRef<() => Promise<void>>(async () => {})
  const [handleClickBatchFractionable, RemoveBatchFractionableModalComponent] = useDeleteBatch({
    onRemove: () => refetchCurrentPage.current(),
  })
  const [lotFraction, setLotFraction] = useState<Batch>()
  const lotRef = useRef<HTMLInputElement>()
  const { fc, validateFc } = useFulfillmentCenter()
  const dispatch = useDispatch()
  const { isLocalRaspberryConnected, isRemotePrinterConnected, fractionTag } = useSelector((state) => state.fraction)

  useEffect(() => {
    validateFc()
  }, [validateFc])

  useEffect(() => {
    if (!lotFraction) {
      dispatch(actions.checkLocalRaspberryStatus())
    }
    const printerInfoData = localStorage.getItem('printerInfo')
    const printerInfo: PrinterData = printerInfoData ? JSON.parse(printerInfoData) : undefined
    if (!lotFraction && printerInfo) {
      dispatch(actions.checkRemotePrinterStatus())
    } else if (!printerInfo) {
      dispatch(actions.clearField('isRemotePrinterConnected'))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotFraction])

  useEffect(() => {
    if (isRemotePrinterConnected && fractionTag === undefined) {
      dispatch(actions.getActiveFractionTagData())
    } else if (!isRemotePrinterConnected) {
      dispatch(actions.clearField('fractionTag'))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRemotePrinterConnected])

  const fetchBatches = useCallback(async (query: BatchesQuery) => {
    lastQuery.current = query
    setIsFetchingBatches(true)

    try {
      const result = await getFractionableBatches({
        fulfillmentCenterId: fc.id,
        ...query,
      })

      if (!result.isOk) {
        toast.error('Falha ao carregar os lotes')
        return
      }

      const { batches } = result.data

      batches.forEach((batch) => {
        formatBatchQuantities(batch)
        batch.actions = (
          <TrashButton handleClick={() => handleClickBatchFractionable(batch)} />
        )
      })

      setDataTables(batches)
      setPagination(result.data.pagination)
    } finally {
      setIsFetchingBatches(false)
    }
  }, [fc.id, handleClickBatchFractionable])

  const updateBatchTable = useCallback(
    async () => fetchBatches(lastQuery.current),
    [fetchBatches],
  )

  refetchCurrentPage.current = updateBatchTable

  const getPaginatedItems = (page: number, perPage: number, order?: string, orderBy?: string | number) => {
    const hasSortSelected = Boolean(orderBy)

    fetchBatches({
      page,
      perPage,
      order: hasSortSelected ? (order as string) : DEFAULT_ORDER,
      orderBy: hasSortSelected ? String(orderBy) : DEFAULT_ORDER_BY,
    })
  }

  const initializeBatchTable = useCallback(async () => {
    await updateBatchTable()

    lotRef.current?.focus()
  }, [updateBatchTable])

  useEffect(() => {
    initializeBatchTable()
  }, [initializeBatchTable])

  const handleFractionate = async (batchId) => {
    const result = await getFractionableBatch({
      batchId,
      fulfillmentCenterId: fc.id,
    })

    if (result.isOk) {
      const formattedBatch = formatBatchQuantities(result.data)
      setLotFraction(formattedBatch)
      setReceivementId(result.data.id_recebimento)
      setModelId(result.data.id_modelo)
    } else {
      toast.error(result.data.message)
      setLotFraction(undefined)
      setReceivementId(undefined)
    }
  }

  const onRowDoubleClick = (row) => {
    handleFractionate(row.id_lote)
  }

  const handleFilter = async (values) => {
    await handleFractionate(values.lote)
  }

  const resetFraction = () => {
    setLotFraction(undefined)
  }

  return (
    <>
      <Loading loading={load} style={{ zIndex: '999' }} />
      <S.HeaderContainer>
        <S.SubHeaderContainer>
          <Text textType='header' text='fraction' />
          <S.HeaderPauseAndIconContainer>
            {lotFraction && (
              <PauseFractioning idBatch={lotFraction.id_lote} onPause={resetFraction} />
            )}
            {(lotFraction && (isLocalRaspberryConnected || isRemotePrinterConnected)) && (
              <S.HeaderIconContainer>
                <S.HeaderIcon src={RaspberryIcon} alt='header icon' data-testid='header-icon-test-id' />
              </S.HeaderIconContainer>
            )}
          </S.HeaderPauseAndIconContainer>
        </S.SubHeaderContainer>
      </S.HeaderContainer>
      {lotFraction ? (
        <FlexLayout>
          <FractionLot
            receivementId={receivementId}
            modelId={modelId}
            batch={lotFraction}
            resetFraction={resetFraction}
            updateTableLot={updateBatchTable}
            setLoad={setLoad}
          />
        </FlexLayout>
      ) : (
        <>
          <S.FormContainer style={{ width: '97%' }}>
            <Formik
              initialValues={{
                lote: '',
              }}
              validateOnChange={false}
              validateOnBlur={false}
              validateOnMount
              onSubmit={handleFilter}
            >
              {() => (
                <Form style={{ width: '350px' }}>
                  <S.FormSubContainer>
                    <FormField
                      fieldRef={lotRef}
                      label='fraction.lot'
                      name='lote'
                    />
                    <Button
                      text='filter'
                      type='submit'
                      style={{ height: '40px', marginTop: '20px' }}
                    />
                  </S.FormSubContainer>
                </Form>
              )}
            </Formik>
            <PrinterInfo />
          </S.FormContainer>
          <FlexLayout style={{ margin: '20px 0 0 0', width: '98%' }}>
            <TableMui
              columns={[
                { id: 'id_lote', label: 'Id Lote', width: 80 },
                { id: 'nome_fornecedor', label: 'Fornecedor', width: 200 },
                { id: 'cod_produto', label: 'Cód. Produto', width: 110 },
                { id: 'descricao', label: 'Descrição', width: 350 },
                { id: 'quantidade_total', label: 'Total', width: 80 },
                { id: 'quantidade_rejeitada', label: 'Rejeitado', width: 90 },
                { id: 'quantidade_util', label: 'Util', width: 80 },
                { id: 'status_fracionamento', label: 'Status', width: 150 },
                { id: 'data_inclusao', label: 'Data Inclusão', width: 110 },
                {
                  id: 'actions', label: 'Ação', width: 70, useSorting: false,
                },
              ]}
              rows={dataTable}
              rowCount={dataTable.length}
              emptyLabel='Sem registros'
              maxHeight='65vh'
              minHeight='65vh'
              enablePagination
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              rowsPerPageLimit={pagination.perPage}
              currentPage={pagination.currentPage - 1}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              getPaginatedItems={getPaginatedItems}
              resetPageOnRowsChange={false}
              isLoading={isFetchingBatches}
              onRowDoubleClick={onRowDoubleClick}
              hasSorting
              thinLine
            />
          </FlexLayout>
          {RemoveBatchFractionableModalComponent(lotFraction)}
        </>
      )}
    </>
  )
}

export default Fraction
